'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import clsx from 'clsx';

import {Skeleton} from '@/modules/finkn/components/skeleton';

import {cn} from '../../shared/tailwind';
import {MinusCircleIcon} from '../icons/minus-circle-icon';
import {PlusCircleIcon} from '../icons/plus-circle-icon';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({className, ...props}, ref) => (
	<AccordionPrimitive.Item
		ref={ref}
		className={cn('border-b', 'border-grey-80', 'first-of-type:border-t', className)}
		{...props}
	/>
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({className, children, ...props}, ref) => (
	<AccordionPrimitive.Header className="flex">
		<AccordionPrimitive.Trigger
			ref={ref}
			className={cn(
				[
					'flex',
					'flex-1',
					'group',
					'items-start',
					'justify-between',
					'py-6',
					'font-semibold',
					'text-base',
					'text-left',
					'gap-8',
					'hover:text-primary-15',
					'transition-colors',
				],
				className,
			)}
			{...props}
		>
			{children}
			<span
				className={clsx(
					'relative',
					'flex',
					'size-6',
					'items-center',
					'gap-2',
					'shrink-0',
					'mt-[3px]',
				)}
			>
				<PlusCircleIcon
					aria-hidden
					className={clsx(
						'shrink-0',
						'transition-all',
						'group-data-[state=open]:opacity-0',
						'group-data-[state=closed]:rotate-180',
						'text-grey-15',
						'text-[24px]',
						'absolute',
						'inset-0',
					)}
				/>
				<MinusCircleIcon
					aria-hidden
					className={clsx(
						'shrink-0',
						'transition-all',
						'group-data-[state=closed]:opacity-0',
						'group-data-[state=open]:rotate-180',
						'text-error-35',
						'text-[24px]',
						'absolute',
						'inset-0',
					)}
				/>
			</span>
		</AccordionPrimitive.Trigger>
	</AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({className, children, ...props}, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className="overflow-hidden text-sm transition-all data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown"
		{...props}
	>
		<div className={cn('pb-4 pt-0', className)}>{children}</div>
	</AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

const AccordionItemSkeleton = () => (
	<Skeleton className="border-b">
		<div className="flex items-center justify-between py-4 font-medium">
			<Skeleton className="w-1/2" />
			<PlusCircleIcon
				className={clsx(
					'size-6',
					'shrink-0',
					'transition-transform',
					'group-data-[state=open]:hidden',
					'text-grey-15',
				)}
			/>
		</div>
	</Skeleton>
);

export {Accordion, AccordionContent, AccordionItem, AccordionItemSkeleton, AccordionTrigger};
